import React, { useState } from "react";
import { base44 } from "@/api/base44Client";
import { AlertCircle, Lock, Mail, Shield } from "lucide-react";
import { toast } from "sonner";

export default function Login({ onLoginSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      const user = await base44.auth.login(email, password);
      toast.success(`Welcome back, ${user.full_name || user.email}.`);
      onLoginSuccess(user);
    } catch (err) {
      const message = err.message || "Sign-in failed. Please check your details and try again.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-tr from-slate-900 via-slate-850 to-slate-950 px-4 py-10">
      <section className="max-w-md w-full bg-slate-900/60 border border-slate-800 backdrop-blur-xl rounded-2xl shadow-2xl p-8">
        <header className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600/10 border border-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/5">
            <Shield className="w-8 h-8 text-blue-500" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-slate-100 tracking-tight">Complete Law Support</h1>
          <p className="text-sm text-slate-400 mt-1.5">Secure client portal</p>
        </header>

        {error && (
          <div role="alert" className="bg-red-500/10 border border-red-500/20 rounded-xl p-3.5 flex items-start gap-3 mb-6">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-red-300 leading-normal">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="portal-email" className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Email address
            </label>
            <div className="relative">
              <Mail className="absolute left-3.5 top-3.5 h-5 w-5 text-slate-500 pointer-events-none" aria-hidden="true" />
              <input
                id="portal-email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-xl py-3 pl-11 pr-4 text-slate-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              />
            </div>
          </div>

          <div>
            <label htmlFor="portal-password" className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-3.5 h-5 w-5 text-slate-500 pointer-events-none" aria-hidden="true" />
              <input
                id="portal-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-xl py-3 pl-11 pr-4 text-slate-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white font-semibold py-3.5 rounded-xl shadow-lg shadow-blue-500/10 hover:shadow-blue-500/20 transition-all flex items-center justify-center mt-6"
          >
            {loading ? <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-label="Signing in" /> : "Sign in to portal"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-500">
          Need access or a password reset? Contact your Complete Law Support administrator.
        </p>
      </section>
    </main>
  );
}
